import type { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  Flex,
  Icon,
  IconButton,
  Spacing,
  Text,
  useActions,
  useHover,
} from "@huub-web/ui";
import type { IconName } from "@huub-web/ui/theme";
import type { ThemeSlotPropsMap } from "@huub-web/ui/contexts/ThemeSlotContext";

type Props = ThemeSlotPropsMap["hubStepCard"];

const ICON_MAP: Record<string, IconName> = {
  maximumMortgage: "calculator",
  assessment: "formPlus",
  advisor: "conversation",
  advice: "documentDownload",
  documentsOutgoing: "documentUpload",
  mediation: "signature",
};

/**
 * VEH-branded step card for the hub journey overview.
 * Displays a secondary (orange) icon per scope, "Stap X:" title
 * in primary color, description, and a chevron button.
 */
export const VerenigingEigenHuisStepCard: FC<Props> = ({
  stepNumber,
  scope,
  title,
  description,
  completed,
  action,
  disabled,
}) => {
  const { t } = useTranslation();
  const { handleAction } = useActions();
  const isInteractive = !!action && !disabled;
  const { hasHover, handleEnter, handleLeave } = useHover({
    disabled: !isInteractive,
  });

  const handleClick = () => {
    if (action) {
      handleAction(action);
    }
  };

  const icon = scope ? ICON_MAP[scope] : undefined;

  return (
    <Card
      as={isInteractive ? "button" : "div"}
      border="default"
      padding="l"
      gap="xs"
      disabled={disabled}
      onClick={isInteractive ? handleClick : undefined}
      onMouseEnter={isInteractive ? handleEnter : undefined}
      onMouseLeave={isInteractive ? handleLeave : undefined}
    >
      <Flex justify="space-between">
        <Icon icon={icon} size="l" color="warning" />
        {completed && <Icon icon="checkCircleFilled" color="gradient" />}
      </Flex>
      <Spacing height="xxs" />
      <Flex direction="column">
        <Text variant="displayS" color="primary" bold>
          {t("verenigingEigenHuisStepCard.stepPrefix", { number: stepNumber })}
        </Text>
        <Text variant="displayS" color="primary">
          {title}
        </Text>
      </Flex>
      <Text color="currentColor75" markdown>
        {description}
      </Text>
      {isInteractive && (
        <>
          <Spacing grow />
          <Flex justify="flex-end">
            <IconButton
              as="div"
              icon="chevronRight"
              variant="tertiary"
              size="s"
              rounded
              active={hasHover}
            />
          </Flex>
        </>
      )}
    </Card>
  );
};
